import React, { useState, Suspense, lazy } from 'react';
import './App.css';
import { Navigation } from './components/sections/Navigation';
import { HeroSection } from './components/sections/HeroSection';
import { useContent } from './hooks/useContent';
import { useTheme } from './hooks/useTheme';

// Import section components
import { SectionLayout } from './components/ui/SectionLayout';
import { FeatureGrid } from './components/ui/FeatureGrid';
import { FAQSection } from './components/ui/FAQSection';
import { SolutionsSection } from './components/sections/SolutionsSection';
import { HowItWorksSection } from './components/sections/HowItWorksSection';
import { BenefitsSection } from './components/sections/BenefitsSection';
import { AboutSection } from './components/sections/AboutSection';
import { CTASection } from './components/sections/CTASection';
import { Footer } from './components/sections/Footer';

// Lazy loaded components
const SimpleContactForm = lazy(() => import('./components/forms/SimpleContactForm').then(module => ({ default: module.SimpleContactForm })));
const SimulationSection = lazy(() => import('./components/sections/SimulationSection').then(module => ({ default: module.SimulationSection })));

function App() {
  const [showForm, setShowForm] = useState(false);
  const { content, isLoading, error } = useContent();
  const { getBackgroundClass } = useTheme();
  
  const handleSimulateClick = () => {
    setShowForm(true);
  };
  
  const handleCloseForm = () => {
    setShowForm(false);
  };
  
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${getBackgroundClass('primary')}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-400 mx-auto mb-4" />
          <p className="text-white text-xl">Carregando...</p>
        </div>
      </div>
    );
  }
  
  if (error || !content) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${getBackgroundClass('primary')}`}>
        <div className="text-center">
          <p className="text-red-400 text-xl mb-4">Erro ao carregar a aplicação</p>
          <p className="text-gray-400">{error || 'Conteúdo não disponível'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${getBackgroundClass('primary')}`}>
      <Navigation onSimulateClick={handleSimulateClick} />
      <main>
        <section id="home">
          <HeroSection onSimulateClick={handleSimulateClick} />
        </section>
        
        {showForm && (
          <Suspense fallback={<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400" /></div>}>
            <SimpleContactForm onClose={handleCloseForm} />
          </Suspense>
        )}
        
        {/* Solutions Section */}
        <SolutionsSection onOpenSimulation={handleSimulateClick} />
        
        {/* Value Proposition Section - Services */}
        <section id="services">
          <SectionLayout
            id="value-proposition"
            title={content.valueProposition.title}
            subtitle={content.valueProposition.subtitle}
            description={content.valueProposition.description}
          >
            {content.valueProposition.items && (
              <FeatureGrid items={content.valueProposition.items} />
            )}
          </SectionLayout>
        </section>
        
        {/* Benefits Section */}
        <BenefitsSection />
        
        {/* Simulation Section */}
        <Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400" /></div>}>
          <SimulationSection onOpenSimulation={handleSimulateClick} />
        </Suspense>
        
        {/* How It Works Section */}
        <HowItWorksSection />
        
        {/* About/Mission Section */}
        <section id="about">
          <AboutSection />
        </section>
        
        {/* CTA Section */}
        <CTASection onOpenSimulation={handleSimulateClick} />
        
        {/* FAQ Section */}
        <FAQSection />
      </main>
      
      {/* Footer - Contact */}
      <section id="contact">
        <Footer />
      </section>
    </div>
  );
}

export default App;
